import ProductSwitcher, { capitalize } from './ProductSwitcher';

/** "QR code" never lowercase; custom terms from template used as-is */
export function displayObjectTerm(term) {
  return /^qr\s*code$/i.test(term || '') ? 'QR code' : (term || 'QR code');
}

/** Truncate team name for display (e.g. in success card) */
export function formatTeamNameForDisplay(teamName, maxLen = 30) {
  if (!teamName) return teamName;
  return teamName.length > maxLen ? `${teamName.substring(0, maxLen - 3)}…` : teamName;
}

/**
 * Pure utility for claim flow copy strings.
 * Derives product, pluralLabel, and objectTerm from tag using ProductSwitcher.
 *
 * @param {Object} params
 * @param {Object} params.tag - Tag (with product, claimTerm)
 * @param {Object} params.locStrings - Localization strings for ProductSwitcher
 * @returns {Object} Copy strings for claim options, page sections, buttons, and success
 */
export function getClaimOptionCopy({ tag, locStrings }) {
  const product = tag?.product;
  const switcher = new ProductSwitcher({ locStrings });
  const map = switcher.getProductMap(product);
  const pluralLabel = (map?.pluralName || 'tips').toLowerCase();
  const objectTerm = tag?.claimTerm || 'QR code';
  const obj = displayObjectTerm(objectTerm);
  const cap = capitalize(pluralLabel);

  // ─── Options (choose step) ─────────────────────────────────────────────────
  const individual = {
    title:
      product === 'tipping'
        ? `Activate this ${obj} and receive ${pluralLabel} for just you`
        : `Activate this ${obj} and receive ${pluralLabel}`,
    subtitle: (() => {
      let account = 'your account';
      if (product === 'giving') account = 'your giving account';
      if (product === 'payment') account = 'your payment account';
      const base = `${cap} will go directly to ${account}`;
      return product === 'tipping'
        ? `${base}; use this option only when no team is needed`
        : base;
    })(),
  };

  const existingTeam = {
    title: 'Choose a team',
    description: 'Choose one of your teams on the next step; make sure to check with the team owner first',
  };

  const createTeam = {
    title: 'Create a new team',
    description: `You'll create your team first, then activate this ${obj}`,
  };

  // ─── Page titles & subtitles ───────────────────────────────────────────────
  const choose = {
    title: `Activate this ${obj}`,
    subtitle: `Choose where ${pluralLabel} will go`,
    skipAssignText: `Skip activating this ${obj} for now`,
  };

  const pickTeam = {
    // Same as existingTeam option card — keep in sync if copy changes
    title: existingTeam.title,
    subtitle: `Pick the team that will receive ${pluralLabel} when you activate this ${obj}.`,
    searchPlaceholder: 'Search teams…',
    emptyStateTitle: "Can't find what you want?",
    createNewTeamButton: 'Create a new team',
    showMoreButton: (count) => `Show more teams (currently showing ${count})`,
    memberCountLabel: (count) => (count === 1 ? 'member' : 'members'),
  };

  const createTeamStep = {
    title: createTeam.title,
    settingUpTitle: 'Setting up your team…',
    helperText: 'You will be asked to add team details later',
  };

  const confirmTeam = {
    title: `Activate this ${obj} to start accepting ${pluralLabel} for this team`,
    subtitle: `Once you activate this ${obj}, you can start accepting ${pluralLabel} for this team`,
  };

  const confirmIndividual = {
    title: individual.title,
    subtitle: individual.subtitle,
    selectCurrencyLabel: 'Select your currency',
    selectCurrencyTooltip: 'Pick the currency you want to use. You cannot change it later.',
    selectCurrencyPlaceholder: 'Select your currency',
  };

  // ─── Success (PersonalTagClaimSuccessCard) ──────────────────────────────────
  const success = {
    title: "You're all set",
    whatToDoNext: 'What to do next?',
    nextSteps: {
      branding: {
        title: 'Settings',
        description: 'Add a logo, description, and customise your team.',
        actionLabel: 'Open',
      },
      invite: {
        title: 'Invite Team Members',
        description: 'Share a link so your team can join.',
        actionLabel: 'Open',
      },
      thankYouPage: {
        title: 'Set up your thank you page',
        description: 'Add reviews, bookings, shopping, and loyalty to your thank-you page.',
        actionLabel: 'Open',
      },
    },
    getBody: (displayTeamName) => {
      const base = `You can now start accepting ${pluralLabel} with this active ${obj}.`;
      if (!displayTeamName) return base;
      const target = String(displayTeamName).trim() || 'this team';
      return `You can now start accepting ${pluralLabel} for ${target} with this active ${obj}.`;
    },
    getButtonLabel: (displayTeamName) => {
      if (!displayTeamName) return 'Go to your account';
      const label = String(displayTeamName).trim() || 'Team';
      return `Open ${label}`;
    },
  };

  // ─── Buttons (shared across steps) ────────────────────────────────────────
  const buttons = {
    continueWithoutSetup: 'Continue without setup',
    confirmAndContinue: 'Activate now',
    back: 'Back',
  };

  // ─── Loading states ───────────────────────────────────────────────────────
  const loading = {
    settingUpAccount: 'Setting up your account…',
    settingUpAccountHint: 'This usually takes a few seconds. If it takes longer, try refreshing the page.',
  };

  return {
    // Options (choose step cards)
    individual,
    existingTeam,
    createTeam,

    // Page sections (grouped by step)
    choose,
    pickTeam,
    createTeamStep,
    confirmTeam,
    confirmIndividual,
    success,
    buttons,
    loading,

    // Derived values (for components that need them)
    pluralCapitalized: cap,
    pluralLabel,
    objectTerm, // always truthy (from tag?.claimTerm || 'QR code')
    objectTermDisplay: obj,

    // Legacy flat keys (for backward compatibility during migration)
    chooseTitle: choose.title,
    chooseSubtitle: choose.subtitle,
    skipAssignText: choose.skipAssignText,
    pickTeamSubtitle: pickTeam.subtitle,
    confirmTeamTitle: confirmTeam.title,
    confirmTeamSubtitle: confirmTeam.subtitle,
    confirmIndividualTitle: confirmIndividual.title,
    confirmIndividualSubtitle: confirmIndividual.subtitle,
  };
}
